import CONFIG from "@/config";

// Single audience carries the MCP scope too: `mcp:internal-tools` was moved onto
// the geo API, so the same token serves both the API and (once Lou 2.0 lands)
// the agent. RBAC intersects against the user's role, so a user without it
// simply does not receive it. See the migration plan, decision D4.
export const SCOPE_STRING =
  "openid profile email offline_access read:data write:library mcp:internal-tools";

/**
 * Tokens are cached in localStorage so the session survives a full page reload:
 * the access token is read back directly, and the rotating refresh token renews
 * it silently via the token endpoint -- no redirect, no consent prompt.
 *
 * Memory caching was the original choice for XSS hardening, but it only survives
 * a reload when the silent-auth iframe can run, which needs third-party cookies
 * via an Auth0 custom domain -- and this tenant has none. Without that, memory
 * caching forced a full re-login (and the dev-tenant consent screen) on every
 * refresh, which is unusable. The residual risk is that localStorage tokens are
 * XSS-readable; the long-term hardening is an Auth0 custom domain, after which
 * this can return to "memory". Changing this one value is the whole switch.
 */
export const CACHE_LOCATION: "memory" | "localstorage" = "localstorage";

export const HTTP_TIMEOUT_SECONDS = 20;

export const isAuth0Enabled = (): boolean => CONFIG.auth?.auth0Enabled === true;

export class AuthConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthConfigError";
  }
}

const REQUIRED_KEYS = [
  ["domain", "VITE_AUTH0_DOMAIN"],
  ["clientId", "VITE_AUTH0_CLIENT_ID"],
  ["audience", "VITE_AUTH0_AUDIENCE"],
  ["redirectUri", "VITE_AUTH0_REDIRECT_URI"],
] as const;

/**
 * Only ever called on the Auth0 path -- the legacy path must boot with none of
 * these set. Deliberately does not reject a trailing slash on the audience:
 * Auth0 API identifiers may legitimately end in one. The rule is that the value
 * matches the provisioned identifier exactly, which cannot be checked here, so
 * the only safe local rule is never to normalise it.
 */
export const assertAuthConfig = (): void => {
  const missing = REQUIRED_KEYS.filter(
    ([key]) => !String(CONFIG.auth?.[key] ?? "").trim(),
  ).map(([, envName]) => envName);

  if (missing.length > 0) {
    throw new AuthConfigError(
      `Auth0 is enabled but these are unset: ${missing.join(", ")}`,
    );
  }

  for (const [key, envName] of REQUIRED_KEYS) {
    const value = String(CONFIG.auth[key]);
    if (value !== value.trim()) {
      throw new AuthConfigError(`${envName} has leading or trailing whitespace`);
    }
  }

  const redirectUri = String(CONFIG.auth.redirectUri);
  let parsed: URL;
  try {
    parsed = new URL(redirectUri);
  } catch {
    throw new AuthConfigError(
      `VITE_AUTH0_REDIRECT_URI is not a valid URL: ${redirectUri}`,
    );
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new AuthConfigError(
      `VITE_AUTH0_REDIRECT_URI must be http or https: ${redirectUri}`,
    );
  }
};
